import { ApiBundle, CURRENCIES } from "../paygate"
import { twMerge } from "tailwind-merge"
import { FormattedMessage } from "react-intl"
import { ReactComponent as BorderX } from "assets/images/border-x.svg"
import { ReactComponent as BorderY } from "assets/images/border-y.svg"
import { Button } from "./button"

interface Props {
  plans: ApiBundle[]
  planNames: string[]
  loading: boolean
  selectedPlan: 0 | 1 | 2
  selectPlan: (planIdx: 0 | 1 | 2) => void
  onContinueClick: () => void
  theme: "light" | "dark"
  originalPrices: number[]
}

interface SubscriptionItemProps {
  badge?: string
  label: string
  currency: string
  fullPrice: number
  dayPrice: number | string
  isSelected: boolean
  onClick: () => void
  className?: string
  theme: "light" | "dark"
  originalPrice: number
  originalDayPrice: number | string
}

export const replaceDotWithComma = (n: number | string) => {
  const result = n.toString().replace(".", ",")
  return result.endsWith(",00") ? result.slice(0, -3) : result
}

export function SubscriptionItem(props: SubscriptionItemProps) {
  const {
    theme,
    isSelected,
    badge,
    label,
    currency,
    fullPrice,
    dayPrice,
    onClick,
    originalPrice,
    originalDayPrice,
  } = props

  const fullPriceFormatted = replaceDotWithComma(fullPrice)
  const originalPriceFormatted = replaceDotWithComma(originalPrice)
  const dayPriceFormatted = replaceDotWithComma(dayPrice)
  const originalDayPriceFormatted = replaceDotWithComma(originalDayPrice)
  const discountPercent = Math.round((1 - fullPrice / originalPrice) * 100)

  const bgDefaultClass = theme === "dark" ? "bg-gray-300" : "bg-[#55499B]"
  const bgSelectedClass = theme === "dark" ? "bg-white" : "bg-white"

  const borderDefaultClass =
    theme === "dark" ? "text-gray-300" : "text-[#55499B]"
  const borderSelectedClass = theme === "dark" ? "text-white" : "text-white"

  const textClass = "text-white"
  const textSelectedClass = "text-black"

  const hintSelectedClass = "text-black opacity-60"
  const hintDefaultClass = "text-white opacity-60"

  const borderXClasses = twMerge(
    isSelected ? borderSelectedClass : borderDefaultClass,
    "absolute w-full h-1.5 left-0",
  )
  const borderYClasses = twMerge(
    isSelected ? borderSelectedClass : borderDefaultClass,
    "absolute w-1 h-full",
  )

  const wrapperClasses = twMerge(
    isSelected ? bgSelectedClass : bgDefaultClass,
    isSelected ? textSelectedClass : textClass,
    "h-[90px] w-full relative mb-6 cursor-pointer p-4",
    "flex items-center justify-between max-w-[325px] mx-auto",
    "odd:-rotate-[1deg] even:rotate-[1deg]",
  )

  const discountDefaultClass = theme === "dark" ? "bg-[#5A5A5A]" : "bg-black/10"
  const discountSelectedClass = "bg-violet-190"
  const discountClasses = twMerge(
    isSelected ? discountSelectedClass : discountDefaultClass,
    isSelected ? "rotate-[2deg]" : "-rotate-[4deg]",
    "text-white text-sm leading-[18px] px-3 py-1",
  )

  return (
    <div className={wrapperClasses} onClick={onClick}>
      {badge && (
        <div
          className={twMerge(
            "px-3 h-[20px] flex items-center justify-center absolute right-3 -top-2.5 z-10 text-sm",
            theme === "dark"
              ? "bg-white text-black"
              : "bg-violet-190 text-white",
          )}
        >
          <FormattedMessage id={badge} />
        </div>
      )}
      <div>
        <div className="uppercase font-sans-bold text-xl">
          <FormattedMessage id={label} />
        </div>
        <div className="text-base">
          <span
            className={twMerge(
              "inline-block mr-2 line-through",
              isSelected ? hintSelectedClass : hintDefaultClass,
            )}
          >
            {currency}
            {originalPriceFormatted}
          </span>
          {currency}
          {fullPriceFormatted}
        </div>
      </div>
      <div className={discountClasses}>{discountPercent}% off</div>
      <div>
        <div
          className={twMerge(
            "text-base line-through",
            isSelected ? hintSelectedClass : hintDefaultClass,
          )}
        >
          {currency}
          {originalDayPriceFormatted}
        </div>
        <div className="flex items-end">
          <div className="font-sans-bold text-[14px] mr-[3px] mb-[3px] leading-[14px]">
            {currency}
          </div>
          <div className="font-sans-bold text-2xl leading-[24px]">
            {dayPriceFormatted}
          </div>
        </div>
        <div
          className={twMerge(
            "text-base",
            isSelected ? hintSelectedClass : hintDefaultClass,
          )}
        >
          <FormattedMessage id="paygate.per.day" />
        </div>
      </div>
      <BorderX className={twMerge(borderXClasses, "-top-[5px]")} />
      <BorderX
        className={twMerge(borderXClasses, "-bottom-[5px] -scale-100")}
      />
      <BorderY className={twMerge(borderYClasses, "-left-0.5")} />
      <BorderY className={twMerge(borderYClasses, "-right-0.5 -scale-x-100")} />
    </div>
  )
}

export function Plans(props: Props) {
  const {
    selectPlan,
    selectedPlan,
    planNames,
    plans,
    theme,
    onContinueClick,
    originalPrices,
  } = props
  const selectedPrice = plans.find(
    (plan) => plan.name === planNames[selectedPlan],
  )?.price
  const selectedPeriod = ["week", "month", "year"][selectedPlan]
  return (
    <div className={twMerge("w-full px-4", "text-white")}>
      <h2 className="mb-6 font-quirk text-[32px] leading-[40px] tracking-[0.7px] px-2 text-center">
        Choose your PLAN
      </h2>
      <div className="mb-10">
        {planNames.map((name, idx) => {
          const apiPlan = plans.find((plan) => plan.name === name)

          if (!apiPlan) return null

          return (
            <SubscriptionItem
              key={name}
              label={
                [
                  "paygate.subscription.week",
                  "paygate.subscription.month",
                  "paygate.subscription.year",
                ][idx]
              }
              currency={CURRENCIES[apiPlan.currency]}
              fullPrice={apiPlan.price}
              dayPrice={(apiPlan.price / [7, 30.5, 365][idx]).toFixed(2)}
              isSelected={selectedPlan === idx}
              onClick={() => selectPlan(idx as 0 | 1 | 2)}
              theme={theme}
              originalPrice={originalPrices[idx]}
              originalDayPrice={(
                originalPrices[idx] / [7, 30.5, 365][idx]
              ).toFixed(2)}
            />
          )
        })}
      </div>
      <div className="text-center pb-8">
        <Button
          className="max-w-[290px] -rotate-[4deg] mx-auto mb-6"
          theme={"white"}
          onClick={onContinueClick}
        >
          <FormattedMessage id="continue" />
        </Button>
        <div
          className={twMerge(
            "text-xs leading-[16px] text-center text-white px-4 mx-auto [&>a]:font-serif-italic [&>a]:italic leading-[18px]",
            "text-white",
          )}
        >
          By continuing, you agree to{" "}
          <a href="https://pure.app/content/en/terms" target="_blank">
            Terms and Conditions
          </a>
          ,{" "}
          <a href="https://pure.app/content/en/privacy" target="_blank">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="https://pure.app/content/en/legal/recurring" target="_blank">
            Recurring Payments Policy
          </a>
          . You will be charged ${replaceDotWithComma(selectedPrice ?? 0)} per{" "}
          {selectedPeriod} and subscription starts when your first payment is
          received. It renews automatically unless you cancel at least 24 hours
          before the end of the current subscription period. You can manage your
          subscription in the app settings.
        </div>
      </div>
    </div>
  )
}
